import { gql } from '@apollo/client';
import { useQueryAsUser, useMutationAsUser } from '../graphql.hooks';

export enum METER_USAGE_VALUE_TYPE {
  AUM = 'AUM'
}

export enum METER_USAGE_ENTITY_TYPE {
  AUTO_PILOT_SETTINGS = 'AUTO_PILOT_SETTINGS'
}

export type AutoPilotMasterPortfolio = {
  autoPilotMasterPortfolioKey: number;
  title: string;
  profileImageUrl: string | null;
  profileImageLargeUrl: string | null;
  subtitle: string | null;
};

export type Portfolio = {
  portfolioKey: number;
  portfolioName: string | null;
  brokerName: string | null;
  autoPilotMasterPortfolio: AutoPilotMasterPortfolio | null;
};

export type AutoPilotSettings = {
  autoPilotSettingsKey: number;
  masterPortfolioKey: number;
  masterPortfolio: Portfolio | null;
  slavePortfolio: Portfolio | null;
  setupStatus: string;
};

export type MeterUsage = {
  meterUsageKey: number;
  userKey: number;
  type: METER_USAGE_VALUE_TYPE;
  entityType: METER_USAGE_ENTITY_TYPE;
  entityKey: number;
  value: string;
  metadata: any;
  date: string;
  createdAt: string;
  updatedAt: string;
  autoPilotSettings: AutoPilotSettings | null;
};

export type MeterUsageInput = {
  key?: number;
  type?: METER_USAGE_VALUE_TYPE;
  entityType?: METER_USAGE_ENTITY_TYPE;
  minDate: string;
  maxDate: string;
};

export type MeterUsageResponse = {
  meterUsage: MeterUsage[];
};

export const GQL_USER_METER_USAGE = gql`
  query UserMeterUsage($input: MeterUsageInput!) {
    whoami {
      userKey
      meterUsage(input: $input) {
        meterUsage {
          meterUsageKey
          userKey
          type
          entityType
          entityKey
          value
          metadata
          date
          createdAt
          updatedAt
          autoPilotSettings {
            autoPilotSettingsKey
            masterPortfolioKey
            setupStatus
            masterPortfolio {
              portfolioKey
              portfolioName
              autoPilotMasterPortfolio {
                autoPilotMasterPortfolioKey
                title
                profileImageUrl
                profileImageLargeUrl
                subtitle
              }
            }
            slavePortfolio {
              portfolioKey
              brokerName
            }
          }
        }
      }
    }
  }
`;

export type UserMeterUsageData = {
  whoami: {
    userKey: number;
    meterUsage: MeterUsageResponse;
  };
};

export const useUserMeterUsage = (userKey: number, input: MeterUsageInput, options: any = {}) => {
  return useQueryAsUser(GQL_USER_METER_USAGE, {
    userKey,
    variables: { input },
    ...options,
  });
};

// Subscription Payment Info types
export type PriceTier = {
  flatAmount: number | null;
  flatAmountDecimal: string | null;
  unitAmount: number | null;
  unitAmountDecimal: string | null;
  yearlyAmountDecimal: string | null;
  upTo: number | null;
};

export type SubscriptionPrice = {
  id: string;
  nickname: string | null;
  unitAmount: number | null;
  unitAmountDecimal: string | null;
  annualAUMFeePercent: number | null;
  displayInterval: string;
  billingScheme: string | null;
  interval: string | null;
  intervalCount: number | null;
  tiersMode: string | null;
  usageType: string | null;
  tiers: PriceTier[] | null;
};

export type SubscriptionProduct = {
  name: string;
  prices: SubscriptionPrice[];
};

export type InvoiceConnection = {
  nextInvoiceAmount: number | null;
  outstandingUsageAmount: number | null;
};

export type SubscriptionPaymentMethod = {
  paymentMethodId: string;
  previewText: string | null;
  brand: string | null;
  expired: boolean;
  expiresText: string | null;
};

export type SubscriptionRenewalData = {
  price: SubscriptionPrice;
  renewalText: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
};

export type SubscriptionPaymentInfo = {
  subscriptionId: string;
  products: SubscriptionProduct[];
  defaultPaymentMethod: SubscriptionPaymentMethod | null;
  renewalText: string | null;
  createdAt: string;
  overDueAmount: number | null;
  invoiceConnection: InvoiceConnection | null;
  canCancelImmediately: boolean | null;
  renewalData: SubscriptionRenewalData[] | null;
};

export type SubscriptionPaymentInfoGetResponse = {
  subscriptions: SubscriptionPaymentInfo[];
  paymentMethods: SubscriptionPaymentMethod[];
};

export const GQL_SUBSCRIPTION_PAYMENT_INFO = gql`
  query SubscriptionPaymentInfoGet {
    subscriptionPaymentInfoGet {
      subscriptions {
        subscriptionId
        renewalText
        createdAt
        overDueAmount
        canCancelImmediately
        invoiceConnection {
          nextInvoiceAmount
          outstandingUsageAmount
        }
        defaultPaymentMethod {
          paymentMethodId
          previewText
          brand
          expired
          expiresText
        }
        products {
          name
          prices {
            id
            nickname
            unitAmount
            unitAmountDecimal
            annualAUMFeePercent
            displayInterval
            billingScheme
            interval
            intervalCount
            tiersMode
            usageType
            tiers {
              flatAmount
              flatAmountDecimal
              unitAmount
              unitAmountDecimal
              yearlyAmountDecimal
              upTo
            }
          }
        }
        renewalData {
          renewalText
          currentPeriodStart
          currentPeriodEnd
          price {
            id
            nickname
            displayInterval
            usageType
          }
        }
      }
      paymentMethods {
        paymentMethodId
        previewText
        brand
        expired
        expiresText
      }
    }
  }
`;

export const useSubscriptionPaymentInfo = (userKey: number, options: any = {}) => {
  return useQueryAsUser(GQL_SUBSCRIPTION_PAYMENT_INFO, {
    userKey,
    ...options,
  });
};

// Meter Usage Billable Value
export type MeterUsageBillableValueGetInput = {
  type: METER_USAGE_VALUE_TYPE;
  userKey: number;
  periodStart: string;
  periodEnd: string;
};

export type MeterUsageBillableValueGetResponse = {
  billableValue: number;
};

export const GQL_METER_USAGE_BILLABLE_VALUE_GET = gql`
  mutation MeterUsageBillableValueGet($input: MeterUsageBillableValueGetInput!) {
    meterUsageBillableValueGet(input: $input) {
      billableValue
    }
  }
`;

export const useMeterUsageBillableValueGet = (userKey: number, options: any = {}) => {
  return useMutationAsUser(GQL_METER_USAGE_BILLABLE_VALUE_GET, {
    userKey,
    ...options,
  });
};

// Meter Usage Edit
export type MeterUsageEditInput = {
  meterUsageKey: number;
  value: string;
};

export type MeterUsageEditResponse = {
  meterUsage: MeterUsage;
};

export const GQL_METER_USAGE_EDIT = gql`
  mutation MeterUsageEdit($input: MeterUsageEditInput!) {
    meterUsageEdit(input: $input) {
      meterUsage {
        meterUsageKey
        value
      }
    }
  }
`;

export const useMeterUsageEdit = (userKey: number, options: any = {}) => {
  return useMutationAsUser(GQL_METER_USAGE_EDIT, {
    userKey,
    ...options,
  });
};

// Stripe Meter Events
export type StripeMeterEvent = {
  identifier: string;
  eventName: string;
  payload: any;
  stripeCustomerId: string;
  value: string;
  livemode: boolean;
  stripeCreatedAt: string;
  streamPending: boolean;
  sentToStripeAt: string | null;
  usageAt: string | null;
  usageForDate: string | null;
  createdAt: string;
};

export type StripeMeterEventInput = {
  minDate: string;
  maxDate: string;
};

export type StripeMeterEventResponse = {
  stripeMeterEvents: StripeMeterEvent[];
};

export const GQL_STRIPE_METER_EVENTS = gql`
  query StripeMeterEvents($input: StripeMeterEventInput!) {
    whoami {
      userKey
      stripeMeterEvents(input: $input) {
        stripeMeterEvents {
          identifier
          eventName
          payload
          stripeCustomerId
          value
          livemode
          stripeCreatedAt
          streamPending
          sentToStripeAt
          usageAt
          usageForDate
          createdAt
        }
      }
    }
  }
`;

export type StripeMeterEventsData = {
  whoami: {
    userKey: number;
    stripeMeterEvents: StripeMeterEventResponse;
  };
};

export const useStripeMeterEvents = (userKey: number, input: StripeMeterEventInput, options: any = {}) => {
  return useQueryAsUser(GQL_STRIPE_METER_EVENTS, {
    userKey,
    variables: { input },
    ...options,
  });
};

// Invoice Preview
export type StripeInvoice = {
  id: string;
  paymentIntentId: string | null;
  subscriptionId: string | null;
  customerId: string;
  amountPaid: number | null;
  amountRemaining: number | null;
  status: string | null;
  finalizedAt: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  createdAt: string;
};

export type InvoicePreviewCreateInput = {
  subscriptionIds: string[];
};

export type InvoicePreviewCreateResponse = {
  invoices: StripeInvoice[];
};

export const GQL_INVOICE_PREVIEW_CREATE = gql`
  mutation InvoicePreviewCreate($input: InvoicePreviewCreateInput!) {
    invoicePreviewCreate(input: $input) {
      invoices {
        id
        paymentIntentId
        subscriptionId
        customerId
        amountPaid
        amountRemaining
        status
        finalizedAt
        paidAt
        periodStart
        periodEnd
        createdAt
      }
    }
  }
`;

export const useInvoicePreviewCreate = (userKey: number, options: any = {}) => {
  return useMutationAsUser(GQL_INVOICE_PREVIEW_CREATE, {
    userKey,
    ...options,
  });
};

// Meter Usage Report
export type MeterUsageReportInput = {
  subscriptionId: string;
  idempotencyKey: string;
};

export type MeterUsageReportResponse = {
  success: boolean;
};

export const GQL_METER_USAGE_REPORT = gql`
  mutation MeterUsageReport($input: MeterUsageReportInput!) {
    meterUsageReport(input: $input) {
      success
    }
  }
`;

export const useMeterUsageReport = (userKey: number, options: any = {}) => {
  return useMutationAsUser(GQL_METER_USAGE_REPORT, {
    userKey,
    ...options,
  });
};
